import Footer from "components/Footer/Footer";
import Header from "components/Header/Header";
import React, { useState, useEffect } from "react";
import eye from "../../assets/eye.svg";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUser } from "providers/UserProvider";
import { useInterceptor } from "providers/InterceptorProvider";
import AccountUpdate from "components/Modals/AccountUpdate/AccountUpdate";

const AccountSettings = () => {
  const [showPassword, setShowPassword] = useState([]);
  const { api } = useInterceptor();
  const [organization, setOrganization] = useState(null);
  const { user, updatePersonalInfo, updatePassword } = useUser();
  const [showSuccess, setShowSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  console.log({ user });

  const getOrganization = async () => {
    try {
      const res = await api.get(
        `/organization/getorganization/${user.organizationId}`
      );
      if (res.status === 200) {
        setOrganization(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log({ user });
    if (user?.organizationId) {
      getOrganization();
    }
  }, [user]);

  const personalSchema = Yup.object().shape({
    fullName: Yup.string().required("Full Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    organization: Yup.string().required("Organization is required"),
  });

  const personalFormik = useFormik({
    initialValues: { fullName: "", email: "", organization: "" },
    validationSchema: personalSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!user || !user._id) {
          alert("User data is not available. Please try again later.");
          return;
        }
        await updatePersonalInfo(values, user._id);
        setShowSuccess("personal information");
        // alert("Personal information updated successfully!");
      } catch (error) {
        console.error("Error updating personal info:", error);
        // alert("Failed to update personal information.");
        setError(error.message);
      }
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (user && organization) {
      personalFormik.setValues({
        fullName: `${user.firstName} ${user.lastName}`,
        email: user.email,
        organization: organization.name,
      });
    }
  }, [user, organization]);

  const passwordSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (values.currentPassword === values.newPassword) {
          setError("New and current password cannot be same");
          return;
        }
        await updatePassword(values, user._id);
        setShowSuccess("password");
        // alert("Password updated successfully!");
      } catch (error) {
        console.error("Error updating password:", error);
        // alert("Failed to update password.");
        setError(error.response.data.message);
      }
      setSubmitting(false);
    },
  });

  const handleModalClose = () => {
    setShowSuccess("");
    window.location.reload();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Header />
      <div className="max-w-[1200px] mx-auto">
        {error && (
          <p className="flex justify-between items-center bg-red-100 border-l-2 border-0 mb-5 border-red-400 text-red-700 px-4 py-3">
            {error}
            <span
              className="cursor-pointer"
              onClick={() => {
                setError("");
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 26 26"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </span>
          </p>
        )}
      </div>
      {/* Personal Information Form */}
      <div className="max-w-[1200px] font-inter mx-auto">
        <h1 className="text-[18px] font-semibold mb-2 mt-[26px]">
          Personal Information
        </h1>
        <div className="bg-[#F4F5F6] rounded-[10px] px-7 py-7">
          <form onSubmit={personalFormik.handleSubmit}>
            <div className="flex gap-5">
              {/* Full Name Input */}
              <div className="flex flex-col flex-1">
                <label className="text-[12px] font-medium py-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={personalFormik.values.fullName}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className="px-3 py-2 outline-none text-[12px] font-medium rounded-[10px] border border-[#D9D9D9]"
                  placeholder="Full Name"
                />
                {personalFormik.errors.fullName &&
                  personalFormik.touched.fullName && (
                    <div className="text-red-500 text-xs">
                      {personalFormik.errors.fullName}
                    </div>
                  )}
              </div>

              {/* Email Input */}
              <div className="flex flex-col flex-1">
                <label className="text-[12px] font-medium py-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={personalFormik.values.email}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className="px-3 py-2 outline-none text-[12px] font-medium rounded-[10px] border border-[#D9D9D9]"
                  placeholder="Email"
                />
                {personalFormik.errors.email &&
                  personalFormik.touched.email && (
                    <div className="text-red-500 text-xs">
                      {personalFormik.errors.email}
                    </div>
                  )}
              </div>

              {/* Organization Input */}
              <div className="flex flex-col flex-1">
                <label className="text-[12px] font-medium py-1">
                  Organization
                </label>
                <input
                  type="text"
                  name="organization"
                  value={personalFormik.values.organization}
                  onChange={personalFormik.handleChange}
                  onBlur={personalFormik.handleBlur}
                  className="px-3 py-2 outline-none text-[12px] font-medium rounded-[10px] border border-[#D9D9D9]"
                  placeholder="Organization"
                />
                {personalFormik.errors.organization &&
                  personalFormik.touched.organization && (
                    <div className="text-red-500 text-xs">
                      {personalFormik.errors.organization}
                    </div>
                  )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={personalFormik.isSubmitting}
                className="text-white px-[22px] py-1.5 mt-6 bg-[#55C880] text-[14px] font-medium rounded-[10px]"
              >
                {personalFormik.isSubmitting ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
          {showSuccess === "personal information" && (
            <AccountUpdate
              updateType="personal information"
              onClose={handleModalClose}
            />
          )}
        </div>
      </div>

      {/* Change Password Form */}
      <div className="max-w-[1200px] font-inter mx-auto mb-[400px]">
        <h1 className="text-[18px] font-semibold mb-2 mt-[26px]">
          Change Password
        </h1>
        <div className="bg-[#F4F5F6] rounded-[10px] px-7 py-7">
          <form onSubmit={passwordFormik.handleSubmit}>
            <div className="flex gap-5">
              {["currentPassword", "newPassword", "confirmPassword"].map(
                (field) => (
                  <div className="flex flex-col flex-1 relative" key={field}>
                    <label className="text-[12px] font-medium py-1">
                      {/* {field.replace(/([A-Z])/g, " $1").trim()} */}
                      {field
                        .replace(/([A-Z])/g, " $1")
                        .trim()
                        .replace(/^./, (char) => char.toUpperCase())}
                    </label>
                    <input
                      type={showPassword.includes(field) ? "text" : "password"}
                      name={field}
                      value={passwordFormik.values[field]}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      placeholder="**********"
                      className="px-3 py-2 outline-none text-[12px] font-medium rounded-[10px] border border-[#D9D9D9]"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-11 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={() =>
                        setShowPassword((prev) =>
                          prev.includes(field)
                            ? prev.filter((item) => item !== field)
                            : [...prev, field]
                        )
                      }
                    >
                      {showPassword === field ? (
                        "👁️"
                      ) : (
                        <img src={eye} alt="hide password" />
                      )}
                    </button>
                    {passwordFormik.errors[field] &&
                      passwordFormik.touched[field] && (
                        <div className="text-red-500 text-xs">
                          {passwordFormik.errors[field]}
                        </div>
                      )}
                  </div>
                )
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={passwordFormik.isSubmitting}
                className="text-white px-[22px] py-1.5 mt-6 bg-[#55C880] text-[14px] font-medium rounded-[10px]"
              >
                {passwordFormik.isSubmitting ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
          {showSuccess === "password" && (
            <AccountUpdate updateType="password" onClose={handleModalClose} />
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccountSettings;
